import styled from '@emotion/styled';

const TooltipArrow = styled.div`
  width: 20px;
  height: 20px;

  &::after {
    content: '';
    display: block;
    border-width: 0 2px 2px 0;
    border-style: solid;
    border-color: ${({ theme }) => theme.colors.accent.blue100};
    border-top: 0;
    border-left: 0;
    width: 20px;
    height: 20px;
    background-color: ${({ theme }) => theme.colors.neutral.white100};
  }
`;

type TooltipContentProps = {
  $visible?: boolean;
  $width?: 'default' | 'wide';
};

const TooltipContent = styled.div<TooltipContentProps>`
  visibility: ${({ $visible }) => ($visible ? '' : 'hidden')};
  z-index: 100;
  padding: 1rem;
  border: 2px solid ${({ theme }) => theme.colors.accent.blue100};
  border-radius: ${({ theme }) => theme.radius.md};
  min-width: ${({ $width }) => ($width === 'wide' ? '31.25rem' : '20rem')};
  max-width: 31.25rem;
  color: ${({ theme }) => theme.colors.neutral.black100};
  background-color: ${({ theme }) => theme.colors.neutral.white100};
  font-family: ${({ theme }) => theme.fontFamily.default};
  font-size: 0.875rem;
  font-weight: normal;

  a {
    color: ${({ theme }) => theme.colors.brand.green140};
  }

  /* Overlay to avoid any mouse glitches when rendering */
  &::before {
    content: '';
    position: absolute;
    background: transparent;
  }

  &[data-popper-placement='top'] {
    &::before {
      inset-inline: 0;
      top: 100%;
      width: 100%;
      height: 24px;
    }

    ${TooltipArrow} {
      bottom: 0;

      &::after {
        transform: translateY(10.5px) rotate(45deg);
      }
    }
  }

  &[data-popper-placement='bottom'] {
    &::before {
      inset-inline: 0;
      bottom: 100%;
      width: 100%;
      height: 24px;
    }

    ${TooltipArrow} {
      top: 0;

      &::after {
        transform: translateY(-12.5px) rotate(-135deg);
      }
    }
  }

  &[data-popper-placement='left'] {
    &::before {
      inset-block: 0;
      left: 100%;
      width: 28px;
      height: 100%;
    }

    ${TooltipArrow} {
      right: -20px;

      &::after {
        transform: translateX(-9.5px) rotate(-45deg);
      }
    }
  }

  &[data-popper-placement='right'] {
    &::before {
      inset-block: 0;
      right: 100%;
      width: 28px;
      height: 100%;
    }

    ${TooltipArrow} {
      left: -20px;

      &::after {
        transform: translateX(7.5px) rotate(135deg);
      }
    }
  }
`;

type TooltipTriggerProps = {
  $hover?: boolean;
};

const TooltipTrigger = styled.button<TooltipTriggerProps>`
  margin: 0;
  padding: 0;
  border: 0;
  border-radius: inherit;
  outline: 0;
  font: inherit;
  color: inherit;
  background: none;
  text-align: inherit;
  text-transform: none;

  &:focus-visible {
    outline: 0.125rem dashed ${({ theme }) => theme.colors.brand.orange100};
    outline-offset: 0.25rem;
  }
`;

export { TooltipTrigger, TooltipContent, TooltipArrow };
